import "./vyneFormItems.scss";
import { Card, Col, type GetProps, Row } from "antd";
import { VyneFormSwitch, type VyneFormSwitchProps } from "./vyneFormSwitch/VyneFormSwitch";
import type { CSSProperties } from "react";

type ColProps = Readonly<GetProps<typeof Col>>;
type RowProps = Readonly<GetProps<typeof Row>>;

/**
 * A row within a form that generally contains two {@link ColHalfWidth}-wrapped fields.
 * We leave some space between the fields.
 */
export const RowTwoField = (props: RowProps) => <Row gutter={16} {...props}></Row>;

/**
 * A responsive column within a {@link RowTwoField} containing one field.
 *
 * Usually, the `span: 12` case is engaged, and the field takes up half of the full width.
 * On narrow screens, `span: 24` causes the fields to stack rather than to appear side by side.
 */
export const ColHalfWidth = (props: ColProps) => <Col xs={{ span: 24 }} sm={{ span: 12 }} {...props}></Col>;

interface FormItemGroupBaseProps extends Readonly<React.PropsWithChildren> {
    readonly id?: string;
    readonly className?: string;
    readonly style?: CSSProperties;
}

interface FormItemGroupProps extends FormItemGroupBaseProps {
    readonly innerCard?: boolean;
    readonly legendTitle: string;
}

const WithCard = ({ innerCard, children, className, legendTitle, ...props }: Readonly<FormItemGroupProps>) => {
    return innerCard ? (
        <Card className={`vyne-form-item-group vyne-form-item-group-card ${className ?? ""}`} size="small" {...props}>
            <fieldset>
                <legend>{legendTitle}</legend>
                {children}
            </fieldset>
        </Card>
    ) : (
        <fieldset className={`vyne-form-item-group vyne-form-item-group-non-card ${className ?? ""}`} {...props}>
            <legend>{legendTitle}</legend>
            {children}
        </fieldset>
    );
};

/** A set of fields grouped together under a heading */
export const FormItemGroup = ({ innerCard = true, children, ...props }: Readonly<FormItemGroupProps>) => (
    <WithCard innerCard={innerCard} {...props}>
        {children}
    </WithCard>
);

interface FormSwitchGroupProps extends FormItemGroupBaseProps {
    readonly desc?: React.ReactNode;
    readonly switchProps: VyneFormSwitchProps;
}

/** A set of fields grouped together under a switch */
export const FormSwitchGroup = ({
    children,
    className,
    switchProps,
    desc,
    ...props
}: Readonly<FormSwitchGroupProps>) => (
    <Card
        size="small"
        title={
            <VyneFormSwitch {...switchProps} className={`vyne-form-item-group-header ${switchProps.className ?? ""}`} />
        }
        {...props}
        className={`vyne-form-item-group vyne-form-switch-group vyne-form-item-group-card ${className ?? ""}`}
    >
        <div className="vyne-form-item-group-non-card">
            {desc ? <aside aria-label={`${switchProps.label} Description`}>{desc}</aside> : null}
            {children ? (
                <fieldset>
                    <legend>
                        <span className="vyne-form-item-group-hide-legend">{`${switchProps.label} Additional Settings`}</span>
                    </legend>
                    {children}
                </fieldset>
            ) : null}
        </div>
    </Card>
);
